import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import BasePopup from "./BasePopup";
import { Info } from "lucide-react";

const JobPreferencesPopup = ({ isOpen, onClose, initialValue, onSave }) => {
    const [preferenceText, setPreferenceText] = useState(initialValue || "");
    const [showExample, setShowExample] = useState(false);

    const exampleText = `Feel free to write anything that helps us better match you — from expanding on your resume to listing specific preferences, priorities, or deal-breakers.

Example for specific job preferences for Software Engineer:
• Looking for a backend engineering role.
• Do not match me with companies listed in my resume — this is a strict requirement.
• Prefer working on micro-services architecture with cloud platforms (AWS or GCP).
• Comfortable with Python, Node.js, or Java — open to others.
• Only interested in public or acquired companies — strict requirement.
• Prefer complex tech products (Security/AI), not interested in healthcare or non-tech sectors.`;

    const handleSave = () => {
        onSave(preferenceText);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    const showEditView = () => {
        setShowExample(false);
    };

    const showExampleView = () => {
        setShowExample(true);
    };

    return (
        <BasePopup isOpen={isOpen} onClose={onClose} customClass="max-w-3xl">
            <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Edit Job Preferences</h3>
                <p className="text-gray-600 mt-2">
                    Specify your job preferences to get more accurate matches
                </p>
            </div>

            <div className="mb-6">
                <div className="w-full">
                    <div className="flex border-t border-l border-r border-gray-200 rounded-t-lg overflow-hidden mb-0">
                        <button
                            type="button"
                            onClick={showEditView}
                            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
                                !showExample
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                        >
                            Edit
                            {!showExample && (
                                <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600"></div>
                            )}
                        </button>
                        <div className="relative flex-1 flex">
                            <button
                                type="button"
                                onClick={showExampleView}
                                className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center transition-colors relative ${
                                    showExample
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                <span>Show Full Example</span>
                                <Info className="w-4 h-4 ml-1.5" />
                                {showExample && (
                                    <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600"></div>
                                )}
                            </button>
                        </div>
                    </div>

                    <div
                        className={`border border-gray-200 rounded-b-lg bg-[#e9f2ff] h-[300px] transition-colors ${
                            !showExample ? "hover:border-blue-300 focus-within:border-blue-300" : ""
                        }`}
                    >
                        {showExample ? (
                            <div className="w-full h-[300px] p-4 bg-[#e9f2ff] text-gray-800 text-base whitespace-pre-wrap leading-relaxed overflow-y-auto">
                                {exampleText}
                            </div>
                        ) : (
                            <textarea
                                value={preferenceText}
                                maxLength={10000}
                                onChange={(e) => {
                                    if (e.target.value.length <= 10000) {
                                        setPreferenceText(e.target.value);
                                    }
                                }}
                                placeholder="Help us tailor your job matches by sharing any specific preferences or requirements you have (e.g. certain technologies, growth opportunities, or a particular company culture). The more details you provide, the more accurate your matches will be."
                                className="w-full h-[300px] p-4 bg-[#e9f2ff] text-gray-800 text-base placeholder-gray-400 border-0 outline-none focus:outline-none focus:ring-0 focus:bg-[#e1eefb] resize-none transition-colors leading-relaxed overflow-y-auto"
                            />
                        )}
                    </div>
                </div>

                <div className="mt-3 text-m text-gray-900">
                    <p className="mb-2">
                        📝 <span className="font-medium">Guidance for better matches:</span>
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>This is a free prompt, you can write anything you want!</li>
                        <li>Think about the technologies or tools you’d love to work with</li>
                        <li>Picture your ideal team, company culture, and environment</li>
                        <li>Consider what kind of compensation or benefits truly matter to you</li>
                        <li>Reflect on which industries excite you — and which don’t</li>
                    </ul>
                </div>
            </div>

            <div className="flex space-x-4">
                <Button
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 h-11 text-sm font-medium rounded-lg"
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 h-11 text-sm font-medium rounded-lg"
                    onClick={handleSave}
                >
                    Save Preferences
                </Button>
            </div>
        </BasePopup>
    );
};

export default JobPreferencesPopup;